/*
 * Mern Living · "Will it work in my space?"
 * Builds a true-to-size 3D model of the faux leather hanging chair in the browser
 * (no model files to host), shows it in <model-viewer> with measurement leader lines,
 * and launches AR on phones.
 *
 * Real dimensions (from Mern Living):
 *   hoop Ø 86 cm · leather 60 cm wide · seat 40 cm off the floor
 *   seat bottom -> middle ring 210 cm · ring -> ceiling 30 cm (adjustable to 60 cm)
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------ vector helpers */
  var add = function (a, b) { return [a[0] + b[0], a[1] + b[1], a[2] + b[2]]; };
  var sub = function (a, b) { return [a[0] - b[0], a[1] - b[1], a[2] - b[2]]; };
  var mul = function (a, s) { return [a[0] * s, a[1] * s, a[2] * s]; };
  var dot = function (a, b) { return a[0] * b[0] + a[1] * b[1] + a[2] * b[2]; };
  var cross = function (a, b) { return [a[1] * b[2] - a[2] * b[1], a[2] * b[0] - a[0] * b[2], a[0] * b[1] - a[1] * b[0]]; };
  var len = function (a) { return Math.sqrt(dot(a, a)); };
  var norm = function (a) { var l = len(a) || 1; return [a[0] / l, a[1] / l, a[2] / l]; };
  var lerp = function (a, b, t) { return add(mul(a, 1 - t), mul(b, t)); };
  var linspace = function (a, b, n) { var r = []; for (var i = 0; i < n; i++) r.push(n === 1 ? a : a + (b - a) * i / (n - 1)); return r; };
  var rad = function (d) { return d * Math.PI / 180; };

  /* ------------------------------------------------------------------ geometry */
  function buildChair(above) {
    var RING_Y = 0.40 + 2.10, TOTAL_H = RING_Y + above;
    var R = 0.43, TUBE = 0.0125, TILT = rad(42);
    var D = [0, -Math.sin(TILT), Math.cos(TILT)], X = [1, 0, 0];
    var SEAT_LOW = 0.40, W = 0.60, TH = 0.003;
    var meshes = {};

    function addMesh(mat, P, I, UV, N) {
      if (!N) N = vnormals(P, I);
      if (!UV) UV = P.map(function () { return [0, 0]; });
      (meshes[mat] = meshes[mat] || []).push({ P: P, N: N, UV: UV, I: I });
    }
    function vnormals(P, I) {
      var n = P.map(function () { return [0, 0, 0]; });
      I.forEach(function (t) {
        var fn = cross(sub(P[t[1]], P[t[0]]), sub(P[t[2]], P[t[0]]));
        for (var k = 0; k < 3; k++) { var v = n[t[k]]; v[0] += fn[0]; v[1] += fn[1]; v[2] += fn[2]; }
      });
      return n.map(norm);
    }
    function gridIdx(nu, nv, flip) {
      var I = [];
      for (var i = 0; i < nu - 1; i++) for (var j = 0; j < nv - 1; j++) {
        var a = i * nv + j, b = (i + 1) * nv + j, c = (i + 1) * nv + j + 1, d = i * nv + j + 1;
        if (!flip) { I.push([a, b, c]); I.push([a, c, d]); } else { I.push([a, c, b]); I.push([a, d, c]); }
      }
      return I;
    }
    function tube(path, radius, mat, seg) {
      seg = seg || 12;
      var uvscale = mat === 'rope' ? 1 / 0.016 : 1, n = path.length;
      var T = path.map(function (p, i) {
        var a = path[Math.max(0, i - 1)], b = path[Math.min(n - 1, i + 1)];
        return norm(sub(b, a));
      });
      var ref = Math.abs(T[0][2]) < 0.9 ? [0, 0, 1] : [1, 0, 0];
      var Nn = norm(cross(T[0], ref));
      var P = [], NM = [], UV = [], L = 0;
      for (var i = 0; i < n; i++) {
        if (i > 0) { Nn = norm(sub(Nn, mul(T[i], dot(Nn, T[i])))); L += len(sub(path[i], path[i - 1])); }
        var B = cross(T[i], Nn);
        for (var k = 0; k <= seg; k++) {
          var a = 2 * Math.PI * k / seg, d = add(mul(Nn, Math.cos(a)), mul(B, Math.sin(a)));
          P.push(add(path[i], mul(d, radius))); NM.push(d); UV.push([k / seg, L * uvscale]);
        }
      }
      addMesh(mat, P, gridIdx(n, seg + 1, true), UV, NM);
    }
    function hoopPt(phi, c) { return add(c, add(mul(X, R * Math.cos(phi)), mul(D, R * Math.sin(phi)))); }

    /* leather sling: one profile per lateral position, hoop centre at origin */
    function profile(x) {
      var bb = Math.sqrt(R * R - x * x), B = mul(D, -bb), F = mul(D, bb), rr = TUBE + 0.006;
      function arc(c, a0, a1, n) {
        return linspace(rad(a0), rad(a1), n).map(function (a) { return [x, c[1] + rr * Math.sin(a), c[2] + rr * Math.cos(a)]; });
      }
      var back = arc(B, 250, 60, 14), front = arc(F, 115, -95, 14);
      var tail = add(back[0], [0, -0.035, 0.012]), lip = add(front[front.length - 1], [0, -0.02, -0.012]);
      var P0 = back[back.length - 1], P3 = front[0], k = len(sub(P3, P0)) / 0.86 + 0.25;
      var P1 = add(P0, mul([0, -0.36, 0.03], k)), P2 = add(P3, mul([0, -0.26, -0.20], k));
      var sag = [];
      for (var i = 1; i < 39; i++) {
        var t = i / 39, u = 1 - t;
        sag.push(add(add(mul(P0, u * u * u), mul(P1, 3 * u * u * t)), add(mul(P2, 3 * u * t * t), mul(P3, t * t * t))));
      }
      var pts = [tail].concat(back, sag, front, [lip]);
      return pts.map(function (p) { return [x, p[1], p[2]]; });
    }
    function resample(p, m) {
      var L = [0];
      for (var i = 1; i < p.length; i++) L.push(L[i - 1] + len(sub(p[i], p[i - 1])));
      var out = [], s = [], j = 0;
      for (var q = 0; q < m; q++) {
        var target = L[L.length - 1] * q / (m - 1);
        while (j < L.length - 2 && L[j + 1] < target) j++;
        var t = (target - L[j]) / ((L[j + 1] - L[j]) || 1);
        out.push(lerp(p[j], p[j + 1], Math.min(1, Math.max(0, t)))); s.push(target);
      }
      return { pts: out, arc: s };
    }
    var NX = 31, M = 140, S = [], ARC = [];
    linspace(-W / 2, W / 2, NX).forEach(function (x) { var r = resample(profile(x), M); S.push(r.pts); ARC.push(r.arc); });
    var low = Infinity;
    S.forEach(function (row) { row.forEach(function (p) { low = Math.min(low, p[1]); }); });
    var CY = SEAT_LOW - (low - TH), C = [0, CY, 0];
    S = S.map(function (row) { return row.map(function (p) { return add(p, C); }); });

    var Nrm = S.map(function (row, i) {
      return row.map(function (p, j) {
        var tl = sub(row[Math.min(M - 1, j + 1)], row[Math.max(0, j - 1)]);
        var tx = sub(S[Math.min(NX - 1, i + 1)][j], S[Math.max(0, i - 1)][j]);
        return norm(cross(tl, tx));
      });
    });
    if (Nrm[NX >> 1][M >> 1][1] < 0) Nrm = Nrm.map(function (r) { return r.map(function (n) { return mul(n, -1); }); });
    var TILE = 0.32;
    var UVg = S.map(function (row, i) { return row.map(function (p, j) { return [(p[0] + W / 2) / TILE, ARC[i][j] / TILE]; }); });
    var top = [], bot = [], uvf = [];
    for (var i = 0; i < NX; i++) for (var j = 0; j < M; j++) {
      top.push(add(S[i][j], mul(Nrm[i][j], TH / 2))); bot.push(sub(S[i][j], mul(Nrm[i][j], TH / 2))); uvf.push(UVg[i][j]);
    }
    addMesh('leather', top, gridIdx(NX, M, false), uvf);
    addMesh('leather', bot, gridIdx(NX, M, true), uvf);
    function strip(a, b, uva, flip) {
      var n = a.length, P = a.concat(b), uv = uva.concat(uva.map(function (u) { return [u[0], u[1] + 0.01]; })), I = [];
      for (var i = 0; i < n - 1; i++) {
        var q = [i, i + 1, n + i + 1, n + i];
        if (!flip) { I.push([q[0], q[1], q[2]]); I.push([q[0], q[2], q[3]]); } else { I.push([q[0], q[2], q[1]]); I.push([q[0], q[3], q[2]]); }
      }
      addMesh('edge', P, I, uv);
    }
    var at = function (i, j, s) { return add(S[i][j], mul(Nrm[i][j], s * TH / 2)); };
    var rowOf = function (i, s) { var r = []; for (var j = 0; j < M; j++) r.push(at(i, j, s)); return r; };
    var colOf = function (j, s) { var r = []; for (var i = 0; i < NX; i++) r.push(at(i, j, s)); return r; };
    strip(rowOf(0, 1), rowOf(0, -1), UVg[0], false);
    strip(rowOf(NX - 1, 1), rowOf(NX - 1, -1), UVg[NX - 1], true);
    strip(colOf(0, 1), colOf(0, -1), UVg.map(function (r) { return r[0]; }), true);
    strip(colOf(M - 1, 1), colOf(M - 1, -1), UVg.map(function (r) { return r[M - 1]; }), false);
    [1, NX - 2].forEach(function (ix) {
      tube(S[ix].map(function (p, j) { return add(p, mul(Nrm[ix][j], TH / 2 + 0.0006)); }), 0.0007, 'thread', 4);
    });

    /* hoop */
    tube(linspace(0, 2 * Math.PI, 121).map(function (p) { return hoopPt(p, C); }), TUBE, 'steel', 16);

    /* ropes, middle ring, ceiling hardware */
    var ROPE = 0.0042, att = [];
    [-1, 1].forEach(function (sx) {
      [[0.29, -0.317], [0.41, 0.13]].forEach(function (xb) { att.push(hoopPt(Math.atan2(xb[1], sx * xb[0]), C)); });
    });
    var RZ = att.reduce(function (s, p) { return s + p[2]; }, 0) / att.length;
    var RING_R = 0.042, RING_T = 0.0075;
    tube(linspace(0, 2 * Math.PI, 73).map(function (a) { return [RING_R * Math.cos(a), RING_Y + RING_R * Math.sin(a), RZ]; }), RING_T, 'steel', 16);
    var ringBot = RING_Y - RING_R, ringTop = RING_Y + RING_R, WT = ringBot - 0.012, WB = ringBot - 0.085;
    tube(linspace(0, 1, 500).map(function (h) { return [0.0165 * Math.cos(h * 2 * Math.PI * 14), WT - h * (WT - WB), RZ + 0.0165 * Math.sin(h * 2 * Math.PI * 14)]; }), 0.0026, 'rope', 6);
    tube([[0, WT + 0.002, RZ], [0, WB - 0.002, RZ]], 0.0158, 'rope', 18);
    [-0.0055, 0.0055].forEach(function (dz) {
      var loop = linspace(Math.PI, 0, 30).map(function (t) { return [0.010 * Math.cos(t), ringBot - RING_T + 0.004 + 0.016 * Math.sin(t), RZ + dz]; });
      tube([[loop[0][0], WT, RZ + dz]].concat(loop, [[loop[loop.length - 1][0], WT, RZ + dz]]), ROPE, 'rope', 8);
    });
    var topPt = [0, WB, RZ];
    att.forEach(function (p) {
      var side = p[0] < 0 ? -1 : 1;
      [-0.0052, 0.0052].forEach(function (off) {
        var o = [off, 0, off * 0.6], st = add(add(topPt, [side * 0.007, 0, 0]), mul(o, 0.5)), en = add(add(p, [0, TUBE + 0.005, 0]), o);
        tube(linspace(0, 1, 40).map(function (t) { var q = lerp(st, en, t); q[2] += 0.006 * Math.sin(Math.PI * t); return q; }), ROPE, 'rope', 8);
      });
      var rel = sub(p, C), phi0 = Math.atan2(dot(rel, D), dot(rel, X));
      var td = norm(sub(hoopPt(phi0 + 1e-3, C), hoopPt(phi0 - 1e-3, C)));
      var u = norm(cross(td, [0, 1, 0])), v = cross(td, u), coil = [];
      for (var q = 0; q < 160; q++) {
        var aa = 2 * Math.PI * 4 * q / 159, s = -0.016 + 0.032 * q / 159;
        coil.push(add(add(p, mul(td, s)), mul(add(mul(u, Math.cos(aa)), mul(v, Math.sin(aa))), TUBE + ROPE * 0.9)));
      }
      tube(coil, ROPE, 'rope', 8);
      var a0 = add(p, [0, -TUBE - 0.004, 0]), a1 = add(p, [0, -0.09, 0.006]);
      var tail = linspace(0, 1, 10).map(function (t) { return lerp(a0, a1, t); });
      tube(tail, ROPE, 'rope', 8);
      tube(tail.map(function (q) { return add(q, [0.009, 0.004, 0]); }), ROPE, 'rope', 8);
    });
    var PLATE_R = 0.040, PLATE_T = 0.010, CARA_H = 0.085, caraBot = TOTAL_H - PLATE_T - 0.035 - CARA_H;
    [-0.0055, 0.0055].forEach(function (dx) {
      var a0 = [dx, ringTop - 0.004, RZ], a1 = [dx * 0.6, caraBot + 0.006, 0];
      tube(linspace(0, 1, 30).map(function (t) { return lerp(a0, a1, t); }), ROPE, 'rope', 8);
    });
    tube(linspace(0, 2 * Math.PI, 80).map(function (t) {
      return [0.020 * Math.cos(t) + 0.004 * Math.pow(Math.cos(t), 3), caraBot + CARA_H / 2 + (CARA_H / 2) * Math.sin(t), 0];
    }), 0.0045, 'steel', 10);
    var eyeC = TOTAL_H - PLATE_T - 0.022;
    tube(linspace(0, 2 * Math.PI, 40).map(function (e) { return [0, eyeC + 0.013 * Math.sin(e), 0.013 * Math.cos(e)]; }), 0.004, 'steel', 10);
    tube([[0, eyeC + 0.013, 0], [0, TOTAL_H - PLATE_T, 0]], 0.005, 'steel', 10);
    var pa = linspace(0, 2 * Math.PI, 49), ring = pa.map(function (a) { return [PLATE_R * Math.cos(a), 0, PLATE_R * Math.sin(a)]; });
    var yb = TOTAL_H - PLATE_T, yt = TOTAL_H - 0.0005, n = pa.length, side = [], sideN = [], Is = [];
    ring.forEach(function (r) { side.push(add(r, [0, yb, 0])); sideN.push(mul(r, 1 / PLATE_R)); });
    ring.forEach(function (r) { side.push(add(r, [0, yt, 0])); sideN.push(mul(r, 1 / PLATE_R)); });
    for (var e = 0; e < n - 1; e++) { Is.push([e, e + 1, n + e + 1]); Is.push([e, n + e + 1, n + e]); }
    addMesh('steel', side, Is, null, sideN);
    var cap = [[0, yb, 0]].concat(ring.map(function (r) { return add(r, [0, yb, 0]); })), Ic = [];
    for (e = 0; e < n - 1; e++) Ic.push([0, e + 1, e + 2]);
    addMesh('steel', cap, Ic, null, cap.map(function () { return [0, -1, 0]; }));

    /* headrest pillow + straps + rings */
    var mid = NX >> 1, itop = 0;
    for (j = 0; j < M; j++) if (S[mid][j][1] > S[mid][itop][1]) itop = j;
    var posS = ARC[mid][itop] + 0.18, k = 0;
    while (k < M - 1 && ARC[mid][k] < posS) k++;
    var anchor = S[mid][k], up = norm(sub(S[mid][k - 1], S[mid][k + 1]));
    var pn = Nrm[mid][k]; pn = norm(sub(pn, mul(up, dot(pn, up))));
    var PW = 0.35, PH = 0.22, PT = 0.075, nu = 48, nv = 32;
    var centre = add(anchor, mul(pn, TH / 2 + 0.002));
    var fr = [], bk = [], uvF = [], uvB = [];
    for (i = 0; i < nu; i++) for (j = 0; j < nv; j++) {
      var uu = i / (nu - 1), vv = j / (nv - 1);
      var ex = 1 - Math.pow(Math.abs(2 * uu - 1), 6), ey = 1 - Math.pow(Math.abs(2 * vv - 1), 6);
      var puff = PT * Math.sqrt(Math.max(0, ex * ey));
      var base = add(add(centre, mul(X, (uu - 0.5) * PW)), mul(up, (vv - 0.5) * PH));
      fr.push(add(base, mul(pn, puff + 0.004))); bk.push(add(base, mul(pn, 0.004)));
      uvF.push([uu, 1 - vv]); uvB.push([uu * PW / TILE, vv * PH / TILE]);
    }
    addMesh('pillow', fr, gridIdx(nu, nv, false), uvF);
    addMesh('leather', bk, gridIdx(nu, nv, true), uvB);
    var dArc = ARC[mid][1] - ARC[mid][0];
    [-0.113, 0.113].forEach(function (sxs) {
      var ix = Math.round((sxs + W / 2) / W * (NX - 1)), j0 = itop - 6, j1 = k - Math.floor(PH / 2 / dArc) + 2, seg = [];
      for (var q = j0; q <= j1; q++) { var s = add(S[ix][q], mul(Nrm[ix][q], TH / 2 + 0.0015)); s[0] = sxs; seg.push(s); }
      var sw = 0.034, nn = seg.length, P = [], UV = [], I = [], L = [0];
      for (q = 1; q < nn; q++) L.push(L[q - 1] + len(sub(seg[q], seg[q - 1])));
      seg.forEach(function (s, q) { P.push(sub(s, [sw / 2, 0, 0])); UV.push([0, L[q] / TILE]); });
      seg.forEach(function (s, q) { P.push(add(s, [sw / 2, 0, 0])); UV.push([sw / TILE, L[q] / TILE]); });
      for (q = 0; q < nn - 1; q++) { I.push([q, nn + q + 1, nn + q]); I.push([q, q + 1, nn + q + 1]); }
      addMesh('strap', P, I, UV);
      var rc = add(add(add(centre, mul(X, sxs)), mul(up, PH / 2 + 0.004)), mul(pn, 0.012));
      tube(linspace(0, 2 * Math.PI, 33).map(function (a) { return add(add(rc, mul(X, 0.021 * Math.cos(a))), mul(up, 0.015 * Math.sin(a))); }), 0.0045, 'chrome', 10);
    });

    /* invisible floor marker so the floor (y = 0) is the bottom of the model: shadow + AR placement */
    addMesh('floor', [[-0.001, 0, 0], [0.001, 0, 0], [0, 0, 0.001]], [[0, 2, 1]]);
    return { meshes: meshes, hoopCentre: CY, ringZ: RZ, total: TOTAL_H };
  }

  /* ------------------------------------------------------------------ procedural textures */
  function rng(seed) { return function () { seed = (seed * 1664525 + 1013904223) >>> 0; return seed / 4294967296; }; }
  // tileable value noise, sum of octaves, values 0..1
  function tileNoise(size, cellsList, weights, seed) {
    var out = new Float32Array(size * size), r = rng(seed), wsum = 0;
    cellsList.forEach(function (cells, o) {
      var g = new Float32Array(cells * cells); for (var i = 0; i < g.length; i++) g[i] = r();
      var w = weights[o]; wsum += w;
      for (var y = 0; y < size; y++) for (var x = 0; x < size; x++) {
        var fx = x / size * cells, fy = y / size * cells, x0 = Math.floor(fx), y0 = Math.floor(fy);
        var tx = fx - x0, ty = fy - y0; tx = tx * tx * (3 - 2 * tx); ty = ty * ty * (3 - 2 * ty);
        var x1 = (x0 + 1) % cells, y1 = (y0 + 1) % cells;
        var a = g[y0 * cells + x0], b = g[y0 * cells + x1], c = g[y1 * cells + x0], d = g[y1 * cells + x1];
        out[y * size + x] += w * ((a * (1 - tx) + b * tx) * (1 - ty) + (c * (1 - tx) + d * tx) * ty);
      }
    });
    for (var i2 = 0; i2 < out.length; i2++) out[i2] /= wsum;
    return out;
  }
  function canvasOf(size, fill) {
    var c = document.createElement('canvas'); c.width = c.height = size;
    var ctx = c.getContext('2d'), img = ctx.createImageData(size, size);
    for (var i = 0; i < size * size; i++) { var v = fill(i); img.data[i * 4] = v[0]; img.data[i * 4 + 1] = v[1]; img.data[i * 4 + 2] = v[2]; img.data[i * 4 + 3] = 255; }
    ctx.putImageData(img, 0, 0); return c;
  }
  function normalFromHeight(h, size, strength) {
    return canvasOf(size, function (i) {
      var x = i % size, y = (i / size) | 0;
      var dx = h[y * size + (x + 1) % size] - h[y * size + (x - 1 + size) % size];
      var dy = h[((y + 1) % size) * size + x] - h[((y - 1 + size) % size) * size + x];
      var n = norm([-dx * strength, dy * strength, 1]);
      return [(n[0] * 0.5 + 0.5) * 255, (n[1] * 0.5 + 0.5) * 255, (n[2] * 0.5 + 0.5) * 255];
    });
  }
  var texCache = null;
  function makeTextures() {
    if (texCache) return texCache;
    var S1 = 512;
    var mott = tileNoise(S1, [4, 9], [1, 0.6], 7), grain = tileNoise(S1, [96, 160], [1, 0.7], 11);
    var leather = canvasOf(S1, function (i) { var v = 232 * (0.93 + 0.07 * mott[i]) * (0.965 + 0.05 * grain[i]); return [v, v, v]; });
    var hgt = new Float32Array(S1 * S1); for (var i = 0; i < hgt.length; i++) hgt[i] = grain[i] * 0.6 + mott[i] * 0.05;
    var leatherN = normalFromHeight(hgt, S1, 2.5);

    var S2 = 256, strand = new Float32Array(S2 * S2), fib = new Float32Array(S2 * S2);
    for (var y = 0; y < S2; y++) for (var x = 0; x < S2; x++) {
      var u = x / S2, v = y / S2, ph = ((u * 3 + v * 3) % 1 + 1) % 1;
      strand[y * S2 + x] = Math.pow(Math.sin(Math.PI * ph), 0.6);
      fib[y * S2 + x] = 0.5 + 0.5 * Math.sin(2 * Math.PI * (u * 48 - v * 20));
    }
    var rope = canvasOf(S2, function (i) { var a = (0.045 + 0.05 * strand[i] + 0.02 * fib[i]) * 255; return [a, a, a * 1.08]; });
    var rh = new Float32Array(S2 * S2); for (i = 0; i < rh.length; i++) rh[i] = strand[i] * 0.94 + fib[i] * 0.04;
    var ropeN = normalFromHeight(rh, S2, 8);

    // pillow front: leather, two stitched straps and the MERN mark (neutral; tinted by the leather colour)
    var pc = document.createElement('canvas'); pc.width = 1024; pc.height = 646;
    var p = pc.getContext('2d');
    p.drawImage(leather, 0, 0, 1024, 646);
    var g = p.createRadialGradient(512, 300, 60, 512, 323, 620); g.addColorStop(0, 'rgba(255,255,255,0.10)'); g.addColorStop(1, 'rgba(0,0,0,0.22)');
    p.fillStyle = g; p.fillRect(0, 0, 1024, 646);
    [[112, 205], [745, 838]].forEach(function (s) {
      p.fillStyle = 'rgba(0,0,0,0.09)'; p.fillRect(s[0] - 5, 0, s[1] - s[0] + 10, 646);
      p.fillStyle = 'rgba(255,255,255,0.05)'; p.fillRect(s[0], 0, s[1] - s[0], 646);
      p.strokeStyle = 'rgba(255,255,255,0.55)'; p.lineWidth = 2; p.setLineDash([9, 7]);
      [s[0] + 10, s[1] - 10].forEach(function (lx) { p.beginPath(); p.moveTo(lx, 0); p.lineTo(lx, 646); p.stroke(); });
      p.setLineDash([]);
      p.strokeStyle = 'rgba(0,0,0,0.25)'; p.lineWidth = 3;
      [s[0], s[1]].forEach(function (lx) { p.beginPath(); p.moveTo(lx, 0); p.lineTo(lx, 646); p.stroke(); });
    });
    p.fillStyle = 'rgba(25,22,20,0.82)'; p.textAlign = 'center';
    p.font = '500 46px Jost, "Helvetica Neue", Arial, sans-serif';
    if (p.letterSpacing !== undefined) p.letterSpacing = '6px';
    p.fillText('MERN', 476, 230);
    p.font = 'italic 22px Georgia, serif'; if (p.letterSpacing !== undefined) p.letterSpacing = '1px';
    p.fillText('living', 476, 262);
    p.strokeStyle = 'rgba(0,0,0,0.2)'; p.lineWidth = 4; p.strokeRect(2, 2, 1020, 642);

    texCache = Promise.all([leather, leatherN, rope, ropeN, pc].map(function (c) {
      return new Promise(function (res) { c.toBlob(function (b) { b.arrayBuffer().then(res); }, 'image/png'); });
    })).then(function (bufs) { return { leather: bufs[0], leatherN: bufs[1], rope: bufs[2], ropeN: bufs[3], pillow: bufs[4] }; });
    return texCache;
  }

  /* ------------------------------------------------------------------ GLB writer */
  function lin(c) { c /= 255; return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); }
  var NEUT = lin(232);
  function tintOf(rgb, k) { return rgb.map(function (c) { return Math.min(1, lin(c) / NEUT * (k || 1)); }).concat([1]); }

  function toGLB(model, tex, rgb) {
    var MATS = {
      leather: { tex: 'leather', nrm: 'leatherN', rough: 0.58, metal: 0, tint: tintOf(rgb), ds: true },
      strap: { tex: 'leather', nrm: 'leatherN', rough: 0.5, metal: 0, tint: tintOf(rgb, 0.9), ds: true },
      pillow: { tex: 'pillow', rough: 0.5, metal: 0, tint: tintOf(rgb) },
      edge: { color: rgb.map(function (c) { return lin(c) * 0.55; }), rough: 0.7, metal: 0, ds: true },
      thread: { color: rgb.map(function (c) { return Math.min(1, lin(c) * 1.35 + 0.02); }), rough: 0.8, metal: 0 },
      steel: { color: [0.018, 0.018, 0.02], rough: 0.42, metal: 0.2 },
      chrome: { color: [0.75, 0.74, 0.72], rough: 0.18, metal: 1 },
      rope: { tex: 'rope', nrm: 'ropeN', rough: 0.92, metal: 0 },
      floor: { color: [1, 1, 1], rough: 1, metal: 0, alpha: 0 }
    };
    var chunks = [], byteLen = 0, views = [], accs = [], images = [], textures = [], texIdx = {}, materials = [], prims = [];
    function push(u8, target) {
      while (byteLen % 4) { chunks.push(new Uint8Array(1)); byteLen++; }
      var v = { buffer: 0, byteOffset: byteLen, byteLength: u8.byteLength }; if (target) v.target = target;
      chunks.push(u8); byteLen += u8.byteLength; views.push(v); return views.length - 1;
    }
    function acc(arr, ctype, type, comps, target, minmax) {
      var vi = push(new Uint8Array(arr.buffer), target), a = { bufferView: vi, componentType: ctype, count: arr.length / comps, type: type };
      if (minmax) {
        var mn = [Infinity, Infinity, Infinity], mx = [-Infinity, -Infinity, -Infinity];
        for (var i = 0; i < arr.length; i += 3) for (var k = 0; k < 3; k++) { mn[k] = Math.min(mn[k], arr[i + k]); mx[k] = Math.max(mx[k], arr[i + k]); }
        a.min = mn; a.max = mx;
      }
      accs.push(a); return accs.length - 1;
    }
    function texture(key) {
      if (key in texIdx) return texIdx[key];
      var vi = push(new Uint8Array(tex[key])); images.push({ bufferView: vi, mimeType: 'image/png' });
      textures.push({ source: images.length - 1, sampler: 0 }); texIdx[key] = textures.length - 1; return texIdx[key];
    }
    Object.keys(model.meshes).forEach(function (m) {
      var parts = model.meshes[m], nv = 0, ni = 0;
      parts.forEach(function (p) { nv += p.P.length; ni += p.I.length * 3; });
      var P = new Float32Array(nv * 3), N = new Float32Array(nv * 3), UV = new Float32Array(nv * 2), I = new Uint32Array(ni), vo = 0, io = 0;
      parts.forEach(function (p) {
        p.P.forEach(function (v, i) { P.set(v, (vo + i) * 3); N.set(p.N[i], (vo + i) * 3); UV.set(p.UV[i], (vo + i) * 2); });
        p.I.forEach(function (t) { I[io++] = t[0] + vo; I[io++] = t[1] + vo; I[io++] = t[2] + vo; });
        vo += p.P.length;
      });
      var md = MATS[m], pbr = { metallicFactor: md.metal, roughnessFactor: md.rough };
      if (md.tex) { pbr.baseColorTexture = { index: texture(md.tex) }; pbr.baseColorFactor = md.tint || [1, 1, 1, 1]; }
      else pbr.baseColorFactor = md.color.concat([md.alpha === undefined ? 1 : md.alpha]);
      var mat = { name: m, pbrMetallicRoughness: pbr, doubleSided: !!md.ds };
      if (md.nrm) mat.normalTexture = { index: texture(md.nrm), scale: 1 };
      if (md.alpha !== undefined) mat.alphaMode = 'BLEND';
      materials.push(mat);
      prims.push({
        attributes: { POSITION: acc(P, 5126, 'VEC3', 3, 34962, true), NORMAL: acc(N, 5126, 'VEC3', 3, 34962), TEXCOORD_0: acc(UV, 5126, 'VEC2', 2, 34962) },
        indices: acc(I, 5125, 'SCALAR', 1, 34963), material: materials.length - 1
      });
    });
    while (byteLen % 4) { chunks.push(new Uint8Array(1)); byteLen++; }
    var gltf = {
      asset: { version: '2.0', generator: 'Mern Living hanging chair' }, scene: 0, scenes: [{ nodes: [0] }],
      nodes: [{ mesh: 0, name: 'MernHangingChair' }], meshes: [{ name: 'MernHangingChair', primitives: prims }],
      accessors: accs, bufferViews: views, buffers: [{ byteLength: byteLen }], materials: materials, textures: textures, images: images,
      samplers: [{ magFilter: 9729, minFilter: 9987, wrapS: 10497, wrapT: 10497 }]
    };
    var js = new TextEncoder().encode(JSON.stringify(gltf)), jpad = (4 - js.length % 4) % 4;
    var total = 12 + 8 + js.length + jpad + 8 + byteLen, out = new Uint8Array(total), dv = new DataView(out.buffer), o = 0;
    dv.setUint32(0, 0x46546C67, true); dv.setUint32(4, 2, true); dv.setUint32(8, total, true); o = 12;
    dv.setUint32(o, js.length + jpad, true); dv.setUint32(o + 4, 0x4E4F534A, true); o += 8;
    out.set(js, o); o += js.length; for (var q = 0; q < jpad; q++) out[o++] = 0x20;
    dv.setUint32(o, byteLen, true); dv.setUint32(o + 4, 0x004E4942, true); o += 8;
    chunks.forEach(function (c) { out.set(c, o); o += c.byteLength; });
    return new Blob([out], { type: 'model/gltf-binary' });
  }

  /* ------------------------------------------------------------------ viewer */
  var MV_SRC = 'https://cdn.jsdelivr.net/npm/@google/model-viewer@4.0.0/dist/model-viewer.min.js';
  function ensureModelViewer() {
    if (window.customElements && customElements.get('model-viewer')) return;
    if (document.querySelector('script[data-mern-mv]')) return;
    var s = document.createElement('script'); s.type = 'module'; s.src = MV_SRC; s.setAttribute('data-mern-mv', ''); document.head.appendChild(s);
  }

  function init(root) {
    if (root.__mern) return; root.__mern = true;
    var details = root.querySelector('details'), content = root.querySelector('.accordion__content');
    var stage = root.querySelector('[data-stage]'), rgb = (root.getAttribute('data-rgb') || '137,84,52').split(',').map(Number);
    var above = 0.30, view = 'seat', mv = null, url = null, started = false;
    var $ = function (sel) { return root.querySelector(sel); };

    function setOpenHeight() { if (details.open) content.style.maxHeight = 'none'; }
    details.addEventListener('toggle', function () {
      if (details.open) {
        content.style.maxHeight = content.scrollHeight + 'px';
        setTimeout(setOpenHeight, 320);
        start();
      } else content.style.maxHeight = '0px';
    });

    function rebuild() {
      return makeTextures().then(function (tex) {
        var blob = toGLB(buildChair(above), tex, rgb);
        if (url) URL.revokeObjectURL(url);
        url = URL.createObjectURL(blob);
        mv.src = url;
      });
    }
    function setView(v) {
      view = v;
      $('[data-view="seat"]').setAttribute('aria-pressed', v === 'seat');
      $('[data-view="full"]').setAttribute('aria-pressed', v === 'full');
      if (!mv) return;
      if (v === 'seat') { mv.cameraTarget = '0m 0.85m 0m'; mv.cameraOrbit = '30deg 80deg 2.9m'; }
      else { var h = 2.5 + above; mv.cameraTarget = '0m ' + (h / 2).toFixed(2) + 'm 0m'; mv.cameraOrbit = '28deg 82deg ' + (h * 2.15).toFixed(2) + 'm'; }
    }
    function start() {
      if (started) return; started = true;
      ensureModelViewer();
      mv = document.createElement('model-viewer');
      var attrs = {
        alt: root.getAttribute('data-alt') || '3D model of the hanging chair', 'camera-controls': '', 'touch-action': 'pan-y',
        'shadow-intensity': '0.9', 'shadow-softness': '0.8', exposure: '1.05', 'environment-image': 'neutral',
        'camera-target': '0m 0.85m 0m', 'camera-orbit': '30deg 80deg 2.9m', 'min-camera-orbit': 'auto 20deg auto',
        'max-camera-orbit': 'auto 95deg 7m', 'field-of-view': '32deg', ar: '', 'ar-modes': 'webxr quick-look', 'ar-placement': 'floor', 'ar-scale': 'fixed'
      };
      Object.keys(attrs).forEach(function (k) { mv.setAttribute(k, attrs[k]); });
      mv.innerHTML =
        '<span class="mern3d__dot" slot="hotspot-top" data-k="top" data-position="0 2.65 0"></span>' +
        '<span class="mern3d__dot" slot="hotspot-ring" data-k="ring" data-position="0 2.50 0"></span>' +
        '<span class="mern3d__dot" slot="hotspot-width" data-k="width" data-position="-0.3 1.02 -0.25"></span>' +
        '<span class="mern3d__dot" slot="hotspot-hoop" data-k="hoop" data-position="0.44 0.78 0"></span>' +
        '<span class="mern3d__dot" slot="hotspot-seat" data-k="seat" data-position="0 0.40 0.08"></span>' +
        '<div slot="progress-bar"></div><button slot="ar-button" hidden></button>';
      stage.insertBefore(mv, stage.firstChild);
      mv.addEventListener('load', function () { var l = $('[data-loading]'); if (l) l.hidden = true; setView(view); });
      mv.addEventListener('ar-status', function (e) {
        if (e.detail.status === 'failed') $('[data-armsg]').textContent = root.getAttribute('data-msg-fail');
      });
      rebuild().catch(function () { var l = $('[data-loading]'); if (l) l.textContent = root.getAttribute('data-msg-error'); });
      lines();
    }

    root.querySelectorAll('[data-view]').forEach(function (b) { b.addEventListener('click', function () { setView(b.getAttribute('data-view')); }); });
    $('[data-dims]').addEventListener('click', function () {
      var on = this.getAttribute('aria-pressed') !== 'true'; this.setAttribute('aria-pressed', on); stage.classList.toggle('mern3d--no-dims', !on);
    });
    root.querySelectorAll('[data-above]').forEach(function (b) {
      b.addEventListener('click', function () {
        root.querySelectorAll('[data-above]').forEach(function (o) { o.setAttribute('aria-pressed', o === b); });
        above = parseFloat(b.getAttribute('data-above'));
        $('[data-top-v]').textContent = Math.round(above * 100) + ' cm';
        if (mv) {
          if (mv.updateHotspot) mv.updateHotspot({ name: 'hotspot-top', position: '0 ' + (2.5 + above / 2).toFixed(2) + ' 0' });
          rebuild().then(function () { setView(view); });
        }
      });
    });
    $('[data-ar]').addEventListener('click', function () {
      var msg = $('[data-armsg]');
      if (mv && mv.canActivateAR) { mv.activateAR(); msg.textContent = root.getAttribute('data-msg-ar'); }
      else msg.textContent = root.getAttribute('data-msg-desktop');
    });

    /* measurement leader lines: labels parked at the stage edges, joined to dots on the model */
    function lines() {
      var svg = $('[data-svg]'), NS = 'http://www.w3.org/2000/svg', labels = [].slice.call(root.querySelectorAll('.mern3d__label')), paths = {};
      labels.forEach(function (l) {
        var p = document.createElementNS(NS, 'path'), c = document.createElementNS(NS, 'circle'); c.setAttribute('r', '2');
        svg.appendChild(p); svg.appendChild(c); paths[l.getAttribute('data-k')] = { p: p, c: c };
      });
      (function frame() {
        var sr = stage.getBoundingClientRect();
        if (details.open && sr.width > 0 && sr.height > 0 && mv) {
          var top = 54, bot = sr.height - 10;
          ['l', 'r'].forEach(function (side) {
            var items = labels.filter(function (l) { return l.getAttribute('data-side') === side; }).map(function (l) {
              var k = l.getAttribute('data-k'), dot = mv.querySelector('[data-k="' + k + '"]'), r = dot.getBoundingClientRect();
              var o = { l: l, k: k, x: r.left + r.width / 2 - sr.left, y: r.top + r.height / 2 - sr.top, h: l.offsetHeight, w: l.offsetWidth };
              var inside = r.width > 0 && o.y > 8 && o.y < sr.height - 8 && o.x > 0 && o.x < sr.width;
              l.style.visibility = dot.style.visibility = inside ? 'visible' : 'hidden';
              if (!inside) { paths[k].p.setAttribute('d', ''); paths[k].c.setAttribute('r', '0'); } else paths[k].c.setAttribute('r', '2');
              return inside ? o : null;
            }).filter(Boolean).sort(function (a, b) { return a.y - b.y; });
            var gap = 6, y = top;
            items.forEach(function (it) { it.ly = Math.max(it.y, y + it.h / 2); y = it.ly + it.h / 2 + gap; });
            y = bot;
            for (var i = items.length - 1; i >= 0; i--) { var it = items[i]; it.ly = Math.min(it.ly, y - it.h / 2); y = it.ly - it.h / 2 - gap; }
            items.forEach(function (it) {
              it.l.style.top = it.ly + 'px';
              var edge = side === 'l' ? 10 + it.w : sr.width - 10 - it.w;
              var elbow = side === 'l' ? Math.min(edge + 14, it.x) : Math.max(edge - 14, it.x);
              paths[it.k].p.setAttribute('d', 'M' + it.x + ' ' + it.y + ' L' + elbow + ' ' + it.ly + ' L' + edge + ' ' + it.ly);
              paths[it.k].c.setAttribute('cx', edge); paths[it.k].c.setAttribute('cy', it.ly);
            });
          });
        }
        requestAnimationFrame(frame);
      })();
    }
    if (details.open) { content.style.maxHeight = 'none'; start(); }
  }

  function boot() { document.querySelectorAll('[data-mern-space-3d]').forEach(init); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot); else boot();
  document.addEventListener('shopify:section:load', boot);

  // exposed for testing
  window.MernSpace3D = { buildChair: buildChair, toGLB: toGLB, makeTextures: makeTextures };
})();
